import type { WebViewResponse } from './types/messages';
import { sendToTerminal, logger } from '../terminalLogger';
import { requestConfirmation } from '../globalConfirm';
import { getTimeoutMessage } from './timeoutMessages';
import { LONG_RUNNING_ACTIONS } from './constants';

/**
 * Actions que suportam notificação de continuação quando o timeout expirar
 * 
 * Se uma action está nesta lista e o timeout expirar, o usuário verá
 * um popup perguntando se deseja continuar aguardando.
 * 
 * ✅ ATUALIZADO: Agora usa LONG_RUNNING_ACTIONS como fonte de verdade.
 * Todas as ações longas suportam continuação de timeout.
 */
const CONTINUABLE_ACTIONS = LONG_RUNNING_ACTIONS;

/**
 * Gerencia handlers por requestId, timeouts e correlação requestId→action.
 * Responsável por evitar handlers pendurados e limpar timeouts com segurança.
 * Suporta notificação de continuação para actions de longa duração.
 */
export class RequestTracker {
  private responseHandlers: Record<string, (response: WebViewResponse) => void> = {};
  private requestTimeouts: Record<string, number> = {};
  private requestIdToAction: Record<string, string> = {};
  private timeoutCounts: Record<string, number> = {}; // Rastreia quantos timeouts já ocorreram
  // 🔍 DEBUG: Rastrear quando cada request foi criado
  private requestCreatedAt: Record<string, number> = {};

  mapAction(requestId: string, action: string): void {
    this.requestIdToAction[requestId] = action;
    // 🔍 DEBUG: Registrar timestamp de criação
    this.requestCreatedAt[requestId] = Date.now();
  }

  getAction(requestId: string): string | undefined {
    return this.requestIdToAction[requestId];
  }

  /**
   * Registra handler e timeout padrão; substitui o anterior, se existir.
   * Se a action suportar continuação, mostra notificação ao invés de falhar imediatamente.
   */
  registerHandlerWithTimeout(
    requestId: string,
    callback: (response: WebViewResponse) => void,
    timeoutMs: number,
    onTimeout?: (requestId: string) => void
  ): void {
    this.responseHandlers[requestId] = callback;
    this.timeoutCounts[requestId] = this.timeoutCounts[requestId] || 0;
    
    // 🔇 Log de registro removido para reduzir verbosidade
    
    const setupTimeout = (count: number) => {
      try {
        const toId = window.setTimeout(() => {
          // Verificar se handler ainda existe (pode ter sido resolvido durante o setTimeout)
          if (!this.responseHandlers[requestId]) {
            if (this.requestTimeouts[requestId]) { delete this.requestTimeouts[requestId]; }
            if (this.timeoutCounts[requestId]) { delete this.timeoutCounts[requestId]; }
            return;
          }

          const action = this.requestIdToAction[requestId];
          const isContinuable = action && CONTINUABLE_ACTIONS.has(action);

          if (isContinuable) {
            // Action que suporta continuação
            const totalMinutes = Math.round((timeoutMs * (count + 1)) / 60000);
            const createdAt = this.requestCreatedAt[requestId];
            const ageMs = createdAt ? Date.now() - createdAt : null;
            const ageMinutes = ageMs ? Math.round(ageMs / 60000) : null;
            
            // 🔍 DEBUG: Log detalhado com informações de diagnóstico
            sendToTerminal('⏰ [TRACKER:TIMEOUT] Timeout expirado - perguntando ao usuário', {
              requestId,
              action,
              timeoutMs,
              count: count + 1,
              totalMinutes,
              // 🔍 DEBUG: Informações extras para diagnóstico
              createdAt: createdAt ? new Date(createdAt).toISOString() : 'unknown',
              ageMs,
              ageMinutes,
              pendingHandlers: Object.keys(this.responseHandlers).length,
              allPendingActions: Object.entries(this.requestIdToAction).map(([id, act]) => ({
                requestId: id,
                action: act,
                ageMs: this.requestCreatedAt[id] ? Date.now() - this.requestCreatedAt[id] : null
              }))
            });
            
            logger.warn(`🚨 [DEBUG] TIMEOUT para ${action} (requestId: ${requestId})`, {
              createdAt: createdAt ? new Date(createdAt).toISOString() : 'unknown',
              ageMinutes,
              message: 'Se você não iniciou esta operação, há um handler pendente de operação anterior!'
            });

            // Usar setTimeout(0) para não bloquear o event loop
            setTimeout(async () => {
              // Verificar novamente se handler ainda existe (pode ter resolvido durante o setTimeout)
              if (!this.responseHandlers[requestId]) {
                sendToTerminal('✅ [TRACKER:TIMEOUT] Resposta chegou durante confirmação', { requestId });
                return;
              }

              // Calcular minutos adicionais
              const additionalMinutes = Math.round(timeoutMs / 60000);

              // Obter mensagem de timeout formatada
              const messageData = getTimeoutMessage({
                action,
                totalMinutes,
                additionalMinutes
              });

              // Usar sistema de confirmação centralizado
              const continuar = await requestConfirmation({
                title: messageData.title,
                message: messageData.message,
                confirmLabel: messageData.confirmLabel,
                cancelLabel: messageData.cancelLabel
              });

              if (continuar) {
                sendToTerminal('✅ [TRACKER:TIMEOUT] Usuário escolheu continuar', {
                  requestId,
                  action,
                  newTimeout: count + 2,
                  totalMinutes: Math.round((timeoutMs * (count + 2)) / 60000),
                });

                // Incrementar contador e configurar novo timeout
                this.timeoutCounts[requestId] = count + 1;
                setupTimeout(count + 1);
              } else {
                // Usuário cancelou
                sendToTerminal('❌ [TRACKER:TIMEOUT] Usuário cancelou', {
                  requestId,
                  action,
                  totalMinutes,
                });

                try {
                  this.responseHandlers[requestId]({
                    requestId,
                    status: 'erro',
                    message: `Operação cancelada pelo usuário após ${totalMinutes} minutos de espera`,
                  } as unknown as WebViewResponse);
                } catch {}

                delete this.responseHandlers[requestId];
                if (this.requestIdToAction[requestId]) { delete this.requestIdToAction[requestId]; }
                if (this.timeoutCounts[requestId]) { delete this.timeoutCounts[requestId]; }
                if (onTimeout) { try { onTimeout(requestId); } catch {} }
              }
            }, 0);
          } else {
            // Action normal - timeout imediato
            sendToTerminal('❌ [TRACKER:TIMEOUT] Timeout - ação não suporta continuação', {
              requestId,
              action,
              timeoutMs,
            });

            try {
              this.responseHandlers[requestId]({
                requestId,
                status: 'erro',
                message: 'timeout',
              } as unknown as WebViewResponse);
            } catch {}

            delete this.responseHandlers[requestId];
            if (this.requestIdToAction[requestId]) { delete this.requestIdToAction[requestId]; }
            if (this.timeoutCounts[requestId]) { delete this.timeoutCounts[requestId]; }
            if (onTimeout) { try { onTimeout(requestId); } catch {} }
          }

          if (this.requestTimeouts[requestId]) { delete this.requestTimeouts[requestId]; }
        }, timeoutMs);

        this.requestTimeouts[requestId] = toId;
      } catch (e) {
        sendToTerminal('⚠️ [TRACKER:TIMEOUT] Erro ao configurar timeout', {
          requestId,
          error: e instanceof Error ? e.message : String(e),
        });
      }
    };

    // Iniciar primeiro timeout
    setupTimeout(0);
  }

  clearTimeoutFor(requestId: string): void {
    const toId = this.requestTimeouts[requestId];
    if (toId) {
      try { window.clearTimeout(toId); } catch {}
      delete this.requestTimeouts[requestId];
    }
    // Limpar contador de timeouts também
    if (this.timeoutCounts[requestId]) {
      delete this.timeoutCounts[requestId];
    }
  }

  /**
   * Resolve uma resposta: chama handler, limpa timeout e, se não for progress_update ou progress, remove handler.
   * 
   * Mantém handler ativo para:
   * - status: "progress_update" (formato antigo usado por outras actions)
   * - type: "progress" (formato novo usado por monitorar-objetos)
   */
  resolveResponse(response: WebViewResponse): void {
    const requestId = (response as any)?.requestId as string | undefined;
    if (!requestId) return;
    const handler = this.responseHandlers[requestId];
    if (handler) {
      const action = this.requestIdToAction[requestId];
      const createdAt = this.requestCreatedAt[requestId];
      const ageMs = createdAt ? Date.now() - createdAt : null;
      
      // DEBUG: Log para capturar-objetos-individuais
      if (action === 'capturar-objetos-individuais') {
        sendToTerminal('🔍 [TRACKER] Resolvendo resposta para capturar-objetos-individuais', {
          requestId,
          status: (response as any)?.status,
          hasHandler: !!handler,
          ageMs,
          isProgress: (response as any)?.status === 'progress_update' || (response as any)?.type === 'progress',
        });
      }
      
      try { handler(response); } catch (err) {
        // Log erro no callback para debug
        if (action === 'capturar-objetos-individuais') {
          sendToTerminal('❌ [TRACKER] Erro ao executar callback', {
            requestId,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
      this.clearTimeoutFor(requestId);
      // Verificar tanto status: "progress_update" quanto type: "progress"
      const isProgress = (response as any)?.status === 'progress_update' || 
                        (response as any)?.type === 'progress';
      if (!isProgress) {
        delete this.responseHandlers[requestId];
        if (this.requestIdToAction[requestId]) { delete this.requestIdToAction[requestId]; }
        if (this.timeoutCounts[requestId]) { delete this.timeoutCounts[requestId]; }
        if (this.requestCreatedAt[requestId]) { delete this.requestCreatedAt[requestId]; }
      }
    }
  }

  clearAll(): void {
    // 🔍 DEBUG: Log antes de limpar
    const pendingCount = Object.keys(this.responseHandlers).length;
    if (pendingCount > 0) {
      sendToTerminal('🧹 [TRACKER:CLEAR_ALL] Limpando handlers pendentes', {
        pendingCount,
        pendingActions: Object.entries(this.requestIdToAction).map(([id, action]) => ({
          requestId: id,
          action,
          ageMs: this.requestCreatedAt[id] ? Date.now() - this.requestCreatedAt[id] : null
        })),
        timestamp: new Date().toISOString()
      });
    }
    
    // Não remove o listener global, apenas esvazia os mapas internos
    this.responseHandlers = {};
    // Limpar todos os timeouts pendentes
    Object.keys(this.requestTimeouts).forEach((k) => {
      const id = this.requestTimeouts[k];
      try { window.clearTimeout(id); } catch {}
      delete this.requestTimeouts[k];
    });
    this.requestIdToAction = {};
    this.timeoutCounts = {};
    this.requestCreatedAt = {};
  }

  /**
   * Verifica se há operações pendentes (handlers aguardando resposta)
   * @returns true se há operações pendentes, false caso contrário
   */
  hasPendingRequests(): boolean {
    return Object.keys(this.responseHandlers).length > 0;
  }

  /**
   * Retorna o número de operações pendentes
   * @returns Número de handlers aguardando resposta
   */
  getPendingCount(): number {
    return Object.keys(this.responseHandlers).length;
  }

  /**
   * Retorna informações sobre operações pendentes (para debug)
   * @returns Array com informações das operações pendentes
   */
  getPendingRequestsInfo(): Array<{ 
    requestId: string; 
    action: string | undefined;
    createdAt: string | null;
    ageMs: number | null;
    ageMinutes: number | null;
  }> {
    const now = Date.now();
    return Object.keys(this.responseHandlers).map(requestId => {
      const createdAt = this.requestCreatedAt[requestId];
      const ageMs = createdAt ? now - createdAt : null;
      return {
        requestId,
        action: this.requestIdToAction[requestId],
        createdAt: createdAt ? new Date(createdAt).toISOString() : null,
        ageMs,
        ageMinutes: ageMs ? Math.round(ageMs / 60000) : null
      };
    });
  }
  
  /**
   * 🔍 DEBUG: Loga todos os handlers pendentes no terminal
   * Útil para diagnóstico de handlers "fantasmas"
   */
  logPendingRequests(): void {
    const pending = this.getPendingRequestsInfo();
    if (pending.length === 0) {
      sendToTerminal('📋 [TRACKER:DEBUG] Nenhum handler pendente');
    } else {
      sendToTerminal('📋 [TRACKER:DEBUG] Handlers pendentes', {
        count: pending.length,
        handlers: pending,
        timestamp: new Date().toISOString()
      });
      // Manter console.table para debug visual (apenas em desenvolvimento)
      if (typeof window !== 'undefined' && (window as any).__DEV__) {
        console.table(pending);
      }
      logger.debug('📋 [TRACKER:DEBUG] Handlers pendentes (tabela)', { pending });
    }
  }

  /**
   * Verifica se há operações pendentes para uma action específica
   * @param action - Nome da action a verificar
   * @returns true se há operações pendentes para essa action
   */
  hasPendingRequestsForAction(action: string): boolean {
    return Object.values(this.requestIdToAction).includes(action);
  }
}


